import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { openWhatsApp } from "@/lib/whatsapp";

export default function Contact() {
  const { settings } = useSettings();
  const c = settings?.contact || {};
  const [f, setF] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!f.name || !f.phone || !f.message) return toast.error("Please fill all fields.");
    setSending(true);
    try {
      await api.post("/contact", f);
      toast.success("Thank you! We will get back to you soon.");
      setF({ name: "", phone: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-2 gap-10">
      <div>
        <p className="text-wheat text-xs font-semibold uppercase tracking-widest mb-3">Get in Touch</p>
        <h1 className="font-serif text-4xl font-bold text-forest-dark">Contact Us</h1>
        <ul className="mt-8 space-y-4 text-charcoal/80">
          <li className="flex gap-3"><MapPin className="text-forest shrink-0" /><span>{c.address}</span></li>
          {c.phone && <li className="flex gap-3"><Phone className="text-forest shrink-0" /><span>{c.phone}</span></li>}
          {c.email && <li className="flex gap-3"><Mail className="text-forest shrink-0" /><span className="break-all">{c.email}</span></li>}
        </ul>
        <Button onClick={() => openWhatsApp()} className="mt-6 bg-whatsapp text-white rounded-full h-11 px-6" data-testid="contact-whatsapp">
          <MessageCircle size={18} className="mr-2" /> Chat on WhatsApp
        </Button>
        {c.maps && (
          <a href={c.maps} target="_blank" rel="noreferrer" className="block mt-8 rounded-2xl overflow-hidden border border-forest/10">
            <img src="https://images.unsplash.com/photo-1524661135-423995f22d0b?crop=entropy&cs=srgb&fm=jpg&q=80&w=800" alt="Map" className="w-full h-40 object-cover" />
          </a>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-forest/10 p-6 space-y-4 h-fit">
        <h2 className="font-serif text-xl text-forest-dark">Send a Message</h2>
        <div><Label className="text-sm">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" className="mt-1" /></div>
        <div><Label className="text-sm">Mobile</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="contact-phone" className="mt-1" /></div>
        <div><Label className="text-sm">Message</Label><Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" rows={4} className="mt-1" /></div>
        <Button onClick={submit} disabled={sending} data-testid="contact-submit" className="w-full bg-forest text-cream rounded-full h-11">Send Message</Button>
      </div>
    </div>
  );
}
